
import React from 'react';
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { TSPEvent } from "@/types/tsp";
import { format, isToday, parseISO } from "date-fns";

interface GameSelectorProps {
  kitNumber: number;
  onGameSelect: (game: TSPEvent) => void;
}

export function GameSelector({ kitNumber, onGameSelect }: GameSelectorProps) {
  const { data: games = [], isLoading } = useQuery({
    queryKey: ['tsp-events'],
    queryFn: async () => {
      const response = await fetch('https://gotsp.live/wp-json/wp/v2/events?status=publish&per_page=100');
      if (!response.ok) {
        throw new Error('Failed to fetch games');
      }
      const data = await response.json();
      return data as TSPEvent[];
    },
  });

  const matchingGames = games.filter(game => {
    // Check if game is today
    const gameDate = parseISO(game.meta.event_date);
    if (!isToday(gameDate)) return false;

    // Extract kit number from stream URL
    const streamUrl = game.meta.event_stream_url;
    if (!streamUrl) return false;

    // Match URLs like https://webplayer-006.b-cdn.net/index.m3u8
    const match = streamUrl.match(/webplayer-(\d{3})/);
    if (!match) return false;

    const gameKitNumber = parseInt(match[1], 10);
    console.log('Comparing kit numbers:', { gameKitNumber, kitNumber });
    return gameKitNumber === kitNumber;
  });

  if (isLoading) {
    return <div>Loading games...</div>;
  }

  if (matchingGames.length === 0) {
    return <div className="text-white">No games found for Kit {kitNumber}</div>;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-white">Today's Games for Kit {kitNumber}</h2>
      <div className="grid gap-4">
        {matchingGames.map((game) => (
          <Button
            key={game.id}
            onClick={() => onGameSelect(game)}
            variant="outline"
            className="w-full justify-start text-left"
          >
            <div className="flex flex-col">
              <span className="font-bold">
                {game.meta.event_away_team_abbreviation} vs {game.meta.event_home_team_abbreviation}
              </span>
              <span className="text-sm text-gray-500">
                {format(parseISO(game.meta.event_date), 'PPP')} at {game.meta.event_time}
              </span>
            </div>
          </Button>
        ))}
      </div>
    </div>
  );
}
